import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { CLICKED_ENTITY_SWITCHER_ON_RECORDS_AND_SETUP } from "constants/analyticsEvents";
import { useAnalytics } from "hooks/useAnalytics";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useIsGroupV2Platform } from "hooks/useIsGroupV2Platform";
import {
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";
import { EntityFlagAvatar } from "v2/components/onboarding/EntityFlagAvatar";

export const EntitySelector = () => {
  const { entities } = useCurrentGroupContext();
  const { entityId = "" } = useParams<{ entityId: string }>();
  const { url } = useRouteMatch();
  const location = useLocation();
  const history = useHistory();
  const isV2Platform = useIsGroupV2Platform();
  const { trackEvent } = useAnalytics();

  const options: OptionData[] = entities.map((entity) => ({
    value: entity.uuid,
    label: entity.name,
  }));

  const countryCodeByEntityId = new Map(
    entities.map((entity) => [entity.uuid, entity.country_code])
  );

  const selectedOption = options.find((option) => option.value === entityId);

  const formatOptionLabel = (option: OptionData) => (
    <span className="t-flex t-min-w-0 t-items-center t-gap-2">
      <EntityFlagAvatar
        countryCode={countryCodeByEntityId.get(option.value)}
        size="xs"
        shape="circle"
      />
      <span className="t-truncate">{option.label}</span>
    </span>
  );

  // swap the :entityId segment in the current path, keep the rest (tab/subnav)
  const goToEntity = (newEntityId: string) => {
    if (!newEntityId || newEntityId === entityId) return;

    const idIndex = url.lastIndexOf(entityId);
    const base = idIndex >= 0 ? url.slice(0, idIndex) : `${url}/`;
    const suffix = location.pathname.slice(url.length);

    history.push(`${base}${newEntityId}${suffix}`);
  };

  // Records & Setup is the index tab of an entity on the v2 platform
  const isRecordsAndSetup = isV2Platform && location.pathname === url;

  const handleMenuOpen = () => {
    if (isRecordsAndSetup) {
      trackEvent(CLICKED_ENTITY_SWITCHER_ON_RECORDS_AND_SETUP);
    }
  };

  const handleChange = (option: unknown) => {
    const selected = option as OptionData | null;
    if (selected?.value) goToEntity(selected.value);
  };

  return (
    <div className="t-w-56 t-shrink-0 sm:t-w-auto sm:t-min-w-0 sm:t-flex-1 sm:t-shrink">
      <Combobox
        withForm={false}
        name="entity-selector"
        value={selectedOption ?? null}
        options={options}
        onChange={handleChange}
        onMenuOpen={handleMenuOpen}
        isClearable={false}
        isSearchable
        formatOptionLabel={formatOptionLabel}
        classNames={{ control: () => "!t-rounded-lg" }}
        menuPortalTarget={document.body}
        placeholder="Select entity"
        size="small"
      />
    </div>
  );
};
